import secureLocalStorage from "react-secure-storage";
import { CUSTOMER_STYLING } from "../../Models/SecureStorageConstants";
import { CustomerStylingModel } from "../../Services/customerStyleService";

export type PdfStatusVariant = "concept" | "pending" | "approved";

export interface IPdfThemeColors {
    primary: [number, number, number];
    primaryText: [number, number, number];
    secondary: [number, number, number];
    good: [number, number, number];
    warning: [number, number, number];
    text: [number, number, number];
    muted: [number, number, number];
    border: [number, number, number];
    white: [number, number, number];
    surface: [number, number, number];
    surfaceStrong: [number, number, number];
    statusBadges: Record<PdfStatusVariant, { bg: [number, number, number]; text: [number, number, number] }>;
}

const DEFAULT_PRIMARY: [number, number, number] = [21, 29, 84];
const DEFAULT_PRIMARY_TEXT: [number, number, number] = [255, 255, 255];
const DEFAULT_SECONDARY: [number, number, number] = [71, 85, 105];
const DEFAULT_GOOD: [number, number, number] = [4, 120, 87];
const DEFAULT_WARNING: [number, number, number] = [194, 65, 12];
const DEFAULT_TEXT: [number, number, number] = [30, 41, 59];
const DEFAULT_MUTED: [number, number, number] = [100, 116, 139];
const DEFAULT_BORDER: [number, number, number] = [226, 232, 240];
const DEFAULT_WHITE: [number, number, number] = [255, 255, 255];

const mixRgb = (
    base: [number, number, number],
    tint: [number, number, number],
    tintWeight: number,
): [number, number, number] => {
    const weight = Math.min(1, Math.max(0, tintWeight));
    return [
        Math.round(base[0] * (1 - weight) + tint[0] * weight),
        Math.round(base[1] * (1 - weight) + tint[1] * weight),
        Math.round(base[2] * (1 - weight) + tint[2] * weight),
    ];
};

const parseColorToRgb = (color: string | undefined, fallback: [number, number, number]): [number, number, number] => {
    if (!color?.trim()) {
        return fallback;
    }

    const canvas = document.createElement("canvas");
    canvas.width = 1;
    canvas.height = 1;
    const context = canvas.getContext("2d");
    if (!context) {
        return fallback;
    }

    try {
        const normalized = color.trim().startsWith("#") ? color.trim() : `#${color.trim()}`;
        context.fillStyle = normalized;
        context.fillRect(0, 0, 1, 1);
        const [red, green, blue] = context.getImageData(0, 0, 1, 1).data;
        return [red, green, blue];
    } catch {
        return fallback;
    }
};

const readCssVarRgb = (varName: string, fallback: [number, number, number]): [number, number, number] => {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
    return parseColorToRgb(raw, fallback);
};

const readStoredCustomerStyling = (): CustomerStylingModel | null => {
    const stored = secureLocalStorage.getItem(CUSTOMER_STYLING);
    if (!stored || typeof stored !== "string") {
        return null;
    }
    try {
        return JSON.parse(stored) as CustomerStylingModel;
    } catch {
        return null;
    }
};

export const resolveProjectPdfTheme = (): IPdfThemeColors => {
    const styling = readStoredCustomerStyling();

    const primary = styling
        ? parseColorToRgb(styling.primaryColor, DEFAULT_PRIMARY)
        : readCssVarRgb("--primary-color", DEFAULT_PRIMARY);
    const primaryText = styling
        ? parseColorToRgb(styling.primaryColorText, DEFAULT_PRIMARY_TEXT)
        : readCssVarRgb("--primary-color-text", DEFAULT_PRIMARY_TEXT);
    const secondary = styling
        ? parseColorToRgb(styling.secondaryColor, DEFAULT_SECONDARY)
        : readCssVarRgb("--secondary-color", DEFAULT_SECONDARY);
    const good = styling ? parseColorToRgb(styling.goodColor, DEFAULT_GOOD) : DEFAULT_GOOD;
    const warning = styling ? parseColorToRgb(styling.wrnColor, DEFAULT_WARNING) : DEFAULT_WARNING;

    return {
        primary,
        primaryText,
        secondary,
        good,
        warning,
        text: DEFAULT_TEXT,
        muted: DEFAULT_MUTED,
        border: DEFAULT_BORDER,
        white: DEFAULT_WHITE,
        surface: mixRgb(DEFAULT_WHITE, primary, 0.04),
        surfaceStrong: mixRgb(DEFAULT_WHITE, primary, 0.1),
        statusBadges: {
            concept: {
                bg: mixRgb(DEFAULT_WHITE, secondary, 0.1),
                text: secondary,
            },
            pending: {
                bg: mixRgb(DEFAULT_WHITE, warning, 0.16),
                text: warning,
            },
            approved: {
                bg: mixRgb(DEFAULT_WHITE, good, 0.14),
                text: good,
            },
        },
    };
};

export const lightenRgb = (color: [number, number, number], amount: number): [number, number, number] =>
    mixRgb(color, DEFAULT_WHITE, amount);
